import { MouseEventHandler, useContext, useMemo } from 'react';
import { useAdCampaignsDataContext } from 'src/contexts/AdCampaignsDataContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { Content } from 'src/contexts/ContentContext';
import {
  getContent,
  useContentContext,
  useFormattedContent,
} from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { PosAnimatingProgressBar } from 'src/core/POS/PosProgressBar/PosAnimatingProgressBar';
import { vars } from 'src/core/themes';
import { ColorsV2 } from 'src/core/themes/constants/colors';
import { Button, Stack } from 'src/core/ui';
import { useGetEventFullInfo } from 'src/hooks/useGetEventFullInfo';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { CampaignModal } from 'src/modals/SponsoredListings/CampaignModal';
import { OpenLinkIcon } from 'src/svgs';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import {
  getEventPerformerVenue,
  getIsStrictlyUSEvent,
} from 'src/utils/eventWithDataUtils';
import { formatCurrency } from 'src/utils/numberFormatter';
import {
  Event,
  FlightDuration,
  Permission,
  SponsoredListingsCampaign,
} from 'src/WebApiController';

import { BoostIconDisplay } from '../BoostIconDisplay';
import { isCampaignActive } from '../SponsoredListings.util';

export type BoostListingButtonProps = {
  campaign?: SponsoredListingsCampaign | null;
  event?: Event;
  shape?: 'pill' | 'rect';
  height?: number | string;
  showIcon?: boolean;
  iconOnly?: boolean;
  backgroundIsPrimary?: boolean;
  disabled?: boolean;
};
export const BoostListingButton = ({
  campaign,
  event: eventFromProps,
  height,
  showIcon,
  iconOnly,
  backgroundIsPrimary,
  disabled,
}: BoostListingButtonProps) => {
  const { setModal } = useContext(ModalContext);
  const { allCampaignData, isEventsEnabledForSponsoredListingsLoading } =
    useAdCampaignsDataContext();

  const hasAdPlatformViewPermission = useUserHasAnyOfPermissions(
    Permission.AdPlatform_CampaignsViewAll
  );
  const hasAdPlatformCreatePermission =
    useUserHasAnyOfPermissions(Permission.AdPlatform_CampaignsCreate) &&
    hasAdPlatformViewPermission;
  const hasAdPlatformEditPermission =
    useUserHasAnyOfPermissions(
      Permission.AdPlatform_CampaignsEdit,
      Permission.AdPlatform_Edit
    ) && hasAdPlatformViewPermission;

  const campaignForEvent = useMemo(() => {
    if (campaign) return campaign;

    if (eventFromProps?.viagId == null || !allCampaignData) {
      return null;
    }

    const allCampaigns = allCampaignData[eventFromProps.viagId] ?? [];

    let activeCampaigns = allCampaigns.filter((c) => isCampaignActive(c));

    if (activeCampaigns.length === 0) {
      activeCampaigns = allCampaigns; // if can't find any campaigns, just return all
    }

    // First try to find a campaign with activeFlight
    const campaignWithActiveFlight = activeCampaigns.find(
      (c) => c.activeFlight
    );

    if (campaignWithActiveFlight) {
      return campaignWithActiveFlight;
    }

    // Fall back to any active campaign
    return activeCampaigns[0] ?? null;
  }, [allCampaignData, campaign, eventFromProps]);

  const onClick: MouseEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!event && !campaignForEvent?.eventId) {
      return;
    }

    setModal({
      children: (
        <CampaignModal
          eventId={event?.viagId ?? campaignForEvent!.eventId!}
          campaign={campaignForEvent}
        />
      ),
      clickOutsideToClose: true,
      size: 'slide-in-md',
    });
  };

  const { data } = useCatalogDataContext();
  const event = useMemo(
    () =>
      eventFromProps ??
      getEventPerformerVenue(campaignForEvent?.eventId?.toString(), data)?.event
        ?.event,
    [campaignForEvent?.eventId, data, eventFromProps]
  );

  const { venue } = useGetEventFullInfo(event);
  const isStrictlyUSEvent = getIsStrictlyUSEvent(venue?.ctryCode);

  const contentContext = useContentContext();

  const spendOverBudgetText = useFormattedContent(
    FormatContentId.ValueOfValue,
    [
      formatCurrency(
        (campaignForEvent?.activeFlight?.flightBudget ?? 0) -
          (campaignForEvent?.activeFlight?.flightSpend ?? 0),
        campaignForEvent?.currencyCode ?? 'USD'
      ),
      formatCurrency(
        campaignForEvent?.activeFlight?.flightBudget,
        campaignForEvent?.currencyCode ?? 'USD'
      ),
    ]
  ) as string;

  const flightDurationText = useMemo(() => {
    const flightDuration = campaignForEvent?.flightDuration;
    switch (flightDuration) {
      case FlightDuration.Daily:
        return getContent(ContentId.Daily, contentContext).toLocaleLowerCase();
      case FlightDuration.Weekly:
        return getContent(
          ContentId.ThisWeek,
          contentContext
        ).toLocaleLowerCase();
      case FlightDuration.Monthly:
        return getContent(
          ContentId.ThisMonth,
          contentContext
        ).toLocaleLowerCase();
      default:
        return getContent(
          ContentId.TheCurrentFlight,
          contentContext
        ).toLocaleLowerCase();
    }
  }, [campaignForEvent?.flightDuration, contentContext]);

  const spendRemainingBudgetText = useFormattedContent(
    FormatContentId.RemainingBudget,
    [flightDurationText]
  ) as string;

  const isAllowedToBoost = useMemo(
    () =>
      (hasAdPlatformCreatePermission || hasAdPlatformEditPermission) &&
      (campaignForEvent || // campaign existence means event is already validated as boost-eligible
        (isStrictlyUSEvent && // only US event is supported
          isEventsEnabledForSponsoredListingsLoading(event?.viagId ?? null))),
    [
      hasAdPlatformCreatePermission,
      hasAdPlatformEditPermission,
      campaignForEvent,
      event?.viagId,
      isEventsEnabledForSponsoredListingsLoading,
      isStrictlyUSEvent,
    ]
  );

  if (!isAllowedToBoost) {
    return null;
  }

  return iconOnly ? (
    <Button
      disabled={disabled}
      variant="outlineBrand"
      shape="rect"
      size="unset"
      style={{
        borderRadius: vars.borderRadius.s,
        padding: vars.spacing.xs,
      }}
      onClick={onClick}
    >
      {showIcon ? (
        <BoostIconDisplay
          size={vars.iconSize.m}
          inactive={!campaignForEvent || !isCampaignActive(campaignForEvent)}
          spendStrategy={campaignForEvent?.spendStrategy}
        />
      ) : (
        <OpenLinkIcon size={vars.iconSize.m} fill={vars.color.textBrandV2} />
      )}
    </Button>
  ) : (
    <Button
      disabled={disabled}
      variant="outlineBrand"
      shape="rect"
      style={{
        height,
        paddingLeft: vars.spacing.sm,
        paddingRight: vars.spacing.sm,
        borderColor:
          campaignForEvent && !isCampaignActive(campaignForEvent)
            ? ColorsV2.error020
            : undefined,
      }}
      color={vars.color.textBrandV2}
      onClick={onClick}
    >
      {campaignForEvent && (
        <Stack
          gap="m"
          alignItems="center"
          style={{ flex: 1 }}
          title={`${spendOverBudgetText} ${spendRemainingBudgetText.toLocaleLowerCase()}`}
        >
          <PosAnimatingProgressBar
            style={{
              width: '80px',
              backgroundColor: backgroundIsPrimary
                ? vars.color.surfaceSecondary
                : vars.color.surfacePrimary,
            }}
            enableGlow
            isError={!isCampaignActive(campaignForEvent)}
            value={
              (((campaignForEvent.activeFlight?.flightBudget ?? 0) -
                (campaignForEvent.activeFlight?.flightSpend ?? 0)) /
                (campaignForEvent.activeFlight?.flightBudget ?? 1)) *
              100
            }
          />
          <span
            style={{
              color: !isCampaignActive(campaignForEvent)
                ? vars.color.textErrorV2
                : vars.color.textBrandV2,
            }}
          >
            {formatCurrency(
              (campaignForEvent.activeFlight?.flightBudget ?? 0) -
                (campaignForEvent.activeFlight?.flightSpend ?? 0),
              campaignForEvent.currencyCode ?? 'USD'
            )}
          </span>
        </Stack>
      )}
      {(showIcon || campaignForEvent) && (
        <BoostIconDisplay
          size={vars.iconSize.m}
          inactive={!campaignForEvent || !isCampaignActive(campaignForEvent)}
          spendStrategy={campaignForEvent?.spendStrategy}
        />
      )}
      {!campaignForEvent && (
        <span style={{ whiteSpace: 'nowrap' }}>
          <Content id={ContentId.BoostEvent} />
        </span>
      )}
    </Button>
  );
};
